import * as React from 'react';
import {
  Avatar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Envelope as EnvelopeIcon,
  Phone as PhoneIcon,
  Trash as TrashIcon,
  UserPlus as UserPlusIcon,
  UsersThree as UsersThreeIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import axios from '@/lib/v2/axios';

import { type User } from '@/types/v2/user';
import { getUnitOwners, removeUserFromUnit } from '@/api/units';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RouterLink } from '@/components/core/v2/link';
import { ProfessionalDialog, DialogActionButtons } from '@/components/common';

interface UnitResidentsSectionProps {
  unitId: string;
}

export function UnitResidentsSection({ unitId }: UnitResidentsSectionProps): React.JSX.Element {
  const { community } = useUser();
  const addUserDialog = useDialog();
  const confirm = useConfirm();

  const { data, isLoading, refetch } = useQuery<{ owners: User[] }>({
    queryKey: ['owners', unitId],
    queryFn: () => getUnitOwners(unitId, { sort: '-move_out_date', current_owner: true }),
  });

  const owners = data?.owners ?? [];

  const { data: usersData, isLoading: usersLoading } = useQuery<{ residents?: User[] } | User[]>({
    queryKey: ['community-residents', community?.id],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/communities/${community?.id}/residents`, { params: { all: 1 } });
      return response.data;
    },
    enabled: Boolean(community?.id),
  });

  const allUsers: User[] = React.useMemo(() => {
    if (!usersData) return [];
    let users: User[] = [];
    if (Array.isArray(usersData)) users = usersData;
    else if ((usersData as any).residents) users = (usersData as any).residents;
    return users.filter((user) => {
      if (user.account_roles?.some((r) => ['account_owner', 'account_admin', 'account_manager'].includes(r.name))) return false;
      if (owners.some((o) => o.id === user.id)) return false;
      return true;
    });
  }, [usersData, owners]);

  const [selectedUserId, setSelectedUserId] = React.useState<string>('');

  const assignUserMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId) throw new Error('Please select a user');
      const response = await axios.post(`/api/v2/units/${unitId}/assign-user`, {
        user_id: selectedUserId,
        role_name: 'unit_resident',
      });
      return response.data;
    },
    onSuccess: async (responseData) => {
      setSelectedUserId('');
      addUserDialog.handleClose();
      toast.success(responseData?.already_assigned ? 'User is already assigned' : 'User assigned successfully');
      await refetch();
    },
  });

  const handleRemove = React.useCallback(
    (resident: User) => {
      confirm({
        title: 'Remove Resident',
        description: `Are you sure you want to remove ${resident.first_name || 'this resident'} from the unit?`,
        confirmationText: 'Remove',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      })
        .then(async () => {
          await removeUserFromUnit(resident.id, unitId);
          toast.success('Resident removed');
          refetch();
        })
        .catch(() => {});
    },
    [unitId, refetch, confirm]
  );

  return (
    <>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
              <UsersThreeIcon size={14} />
            </Box>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
              Residents
            </Typography>
            {owners.length > 0 && (
              <Chip label={owners.length} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} variant="outlined" />
            )}
          </Stack>
          <Button onClick={addUserDialog.handleOpen} size="small" startIcon={<UserPlusIcon size={14} />} variant="outlined">
            Add
          </Button>
        </Stack>

        {isLoading ? (
          <Stack spacing={1}>
            {[1, 2].map((i) => (
              <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
                <Skeleton height={36} variant="circular" width={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton height={14} variant="text" width="50%" />
                  <Skeleton height={12} variant="text" width="40%" />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : owners.length === 0 ? (
          <Typography color="text.disabled" sx={{ fontStyle: 'italic', py: 2 }} variant="body2">
            No residents assigned.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {owners.map((resident: User) => (
              <Stack
                key={resident.id}
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                  py: 1,
                  px: 1.5,
                  borderRadius: 1.5,
                  transition: 'background-color 0.12s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar sx={{ width: 36, height: 36, fontSize: '0.75rem', fontWeight: 700, bgcolor: 'primary.main' }}>
                  {(resident.first_name?.[0] ?? '')}{(resident.last_name?.[0] ?? '')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {resident.uuid ? (
                    <Link
                      component={RouterLink}
                      href={paths.users.details(resident.uuid)}
                      sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                        {resident.first_name} {resident.last_name}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      {resident.first_name} {resident.last_name}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                    {resident.email && (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <EnvelopeIcon size={10} />
                        <Typography noWrap sx={{ fontSize: '0.7rem' }}>{resident.email}</Typography>
                      </Stack>
                    )}
                    {resident.phone && (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <PhoneIcon size={10} />
                        <Typography sx={{ fontSize: '0.7rem' }}>{resident.phone}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
                <Tooltip title="Remove">
                  <IconButton onClick={() => handleRemove(resident)} size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <TrashIcon size={14} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <ProfessionalDialog
        actions={
          <DialogActionButtons
            confirmText={assignUserMutation.isPending ? 'Adding...' : 'Add User'}
            disabled={!selectedUserId}
            loading={assignUserMutation.isPending}
            onCancel={() => { setSelectedUserId(''); addUserDialog.handleClose(); }}
            onConfirm={() => assignUserMutation.mutate()}
          />
        }
        autoHeight
        contentSx={{ pt: 2, px: 3, pb: 2 }}
        icon={<UserPlusIcon size={20} />}
        maxWidth="sm"
        onClose={() => { setSelectedUserId(''); addUserDialog.handleClose(); }}
        open={addUserDialog.open}
        subtitle="Select a community resident to add to this unit"
        title="Add Resident"
      >
        <Autocomplete
          blurOnSelect
          disabled={usersLoading}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return options.filter((o) => {
              const name = `${o.first_name ?? ''} ${o.last_name ?? ''}`.toLowerCase();
              return name.includes(q) || (o.email ?? '').toLowerCase().includes(q);
            });
          }}
          getOptionLabel={(o) => `${o.first_name ?? ''} ${o.last_name ?? ''}`.trim() || o.email || ''}
          loading={usersLoading}
          noOptionsText="No users found"
          onChange={(_e, v) => setSelectedUserId(v?.id || '')}
          options={allUsers}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{ ...params.InputProps, endAdornment: (<>{usersLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>) }}
              label="Search and select user"
              placeholder="Type to search…"
            />
          )}
          renderOption={(props, option) => {
            const name = `${option.first_name ?? ''} ${option.last_name ?? ''}`.trim();
            const initials = `${option.first_name?.[0] ?? ''}${option.last_name?.[0] ?? ''}`.toUpperCase();
            return (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{initials || (option.email?.[0] ?? '?')}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }} variant="body2">{name || option.email}</Typography>
                    <Typography color="text.secondary" noWrap variant="caption">{option.email}</Typography>
                  </Box>
                </Stack>
              </Box>
            );
          }}
          value={allUsers.find((u) => u.id === selectedUserId) || null}
        />
      </ProfessionalDialog>
    </>
  );
}
